import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield, Mail, MessageSquare, Clock, Send, CheckCircle2, Globe, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import SEO, { organizationSchema, createBreadcrumbSchema } from "../components/SEO";

export default function ContatoPage() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'pt';

  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    empresa: "",
    assunto: "",
    mensagem: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Language-aware paths
  const paths = {
    home: '/',
    about: currentLang === 'en' ? '/en/about' : '/pt/sobre',
    privacy: currentLang === 'en' ? '/en/privacy-policy' : '/pt/politica-de-privacidade',
    terms: currentLang === 'en' ? '/en/terms-of-use' : '/pt/termos-de-uso',
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // SEO Configuration
  const seoConfig = currentLang === 'en' ? {
    title: "Contact Us - OmbudNow",
    description: "Get in touch with OmbudNow. Contact our team for questions about whistleblowing channels and corporate compliance.",
    canonical: "/en/contact",
    keywords: "contact ombudnow, whistleblowing support, compliance help"
  } : {
    title: "Contato - OmbudNow",
    description: "Entre em contato com a OmbudNow. Fale com nossa equipe sobre canal de denúncias e compliance corporativo.",
    canonical: "/pt/contato",
    keywords: "contato ombudnow, suporte denúncias, ajuda compliance"
  };

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: currentLang === 'en' ? 'Home' : 'Início', url: '/' },
    { name: currentLang === 'en' ? 'Contact' : 'Contato', url: seoConfig.canonical }
  ]);

  const content = currentLang === 'en' ? {
    title: "Contact Us",
    subtitle: "Have questions? We're here to help. Fill out the form and our team will respond shortly.",
    form: {
      name: "Name",
      namePlaceholder: "Your name",
      email: "Email",
      emailPlaceholder: "your@email.com",
      company: "Company",
      companyPlaceholder: "Company name (optional)",
      subject: "Subject",
      subjectPlaceholder: "How can we help?",
      message: "Message",
      messagePlaceholder: "Tell us more about your needs...",
      submit: "Send message",
      sending: "Sending..."
    },
    info: {
      email: "Email",
      emailValue: "support@ombudnow.com",
      response: "Response Time",
      responseValue: "Within 24 business hours",
      chat: "Chat",
      chatValue: "Available Mon-Fri, 9am-6pm"
    },
    success: {
      title: "Message sent!",
      description: "Thank you for reaching out. Our team will respond within 24 business hours.",
      button: "Send another message"
    }
  } : {
    title: "Entre em Contato",
    subtitle: "Tem dúvidas? Estamos aqui para ajudar. Preencha o formulário e nossa equipe responderá em breve.",
    form: {
      name: "Nome",
      namePlaceholder: "Seu nome",
      email: "Email",
      emailPlaceholder: "seu@email.com",
      company: "Empresa",
      companyPlaceholder: "Nome da empresa (opcional)",
      subject: "Assunto",
      subjectPlaceholder: "Como podemos ajudar?",
      message: "Mensagem",
      messagePlaceholder: "Conte-nos mais sobre suas necessidades...",
      submit: "Enviar mensagem",
      sending: "Enviando..."
    },
    info: {
      email: "Email",
      emailValue: "support@ombudnow.com",
      response: "Tempo de Resposta",
      responseValue: "Até 24 horas úteis",
      chat: "Chat",
      chatValue: "Disponível Seg-Sex, 9h-18h"
    },
    success: {
      title: "Mensagem enviada!",
      description: "Obrigado pelo contato. Nossa equipe responderá em até 24 horas úteis.",
      button: "Enviar outra mensagem"
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setSubmitted(true);
    toast.success(content.success.title);
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white font-ibm">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={seoConfig.canonical}
        lang={currentLang}
        keywords={seoConfig.keywords}
        schemas={[organizationSchema, breadcrumbSchema]}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={paths.home} className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-900 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-slate-900 font-chivo tracking-tight">OMBUDNOW</span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to={currentLang === 'en' ? '/en/about' : '/pt/sobre'} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                {currentLang === 'en' ? 'About' : 'Sobre'}
              </Link>
              <Link to={currentLang === 'en' ? '/en/blog' : '/pt/blog'} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                Blog
              </Link>
              <Link to={currentLang === 'en' ? '/en/contact' : '/pt/contato'} className="text-slate-900 font-medium text-sm">
                {currentLang === 'en' ? 'Contact' : 'Contato'}
              </Link>
              <div className="flex items-center gap-1.5 text-slate-500 text-sm border-l border-slate-200 pl-6 ml-2">
                <Globe className="w-4 h-4" />
                <button 
                  onClick={() => changeLanguage('pt')} 
                  className={`px-1 ${currentLang === 'pt' ? 'text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'} transition-colors`}
                >
                  PT
                </button>
                <span className="text-slate-300">/</span>
                <button 
                  onClick={() => changeLanguage('en')} 
                  className={`px-1 ${currentLang === 'en' ? 'text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'} transition-colors`}
                >
                  EN
                </button>
              </div>
            </nav>

            <Button
              className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-5"
              onClick={() => window.location.href = '/onboarding?plan=free'}
            >
              {currentLang === 'en' ? 'Start free' : 'Começar grátis'}
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 font-chivo tracking-tight">
              {content.title}
            </h1>
            <p className="text-xl text-slate-600">
              {content.subtitle}
            </p>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-12 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Contact Info */}
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{content.info.email}</h3>
                      <a href="mailto:support@ombudnow.com" className="text-blue-600 hover:text-blue-700 transition-colors text-sm">
                        {content.info.emailValue}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{content.info.response}</h3>
                      <p className="text-slate-600 text-sm">{content.info.responseValue}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-violet-50 rounded-xl p-6 border border-violet-100">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{content.info.chat}</h3>
                      <p className="text-slate-600 text-sm">{content.info.chatValue}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="lg:col-span-2">
                {submitted ? (
                  <div className="bg-slate-50 rounded-xl p-12 border border-slate-200 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3 font-chivo">{content.success.title}</h3>
                    <p className="text-slate-600 mb-8">{content.success.description}</p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ nome: "", email: "", empresa: "", assunto: "", mensagem: "" });
                      }}
                      className="border-slate-300"
                    >
                      {content.success.button}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 border border-slate-200 shadow-sm">
                    <div className="grid sm:grid-cols-2 gap-6 mb-6">
                      <div>
                        <Label className="text-slate-700 mb-2 block">{content.form.name} *</Label>
                        <Input
                          name="nome"
                          value={formData.nome}
                          onChange={handleChange}
                          placeholder={content.form.namePlaceholder}
                          required
                          className="border-slate-200"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700 mb-2 block">{content.form.email} *</Label>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder={content.form.emailPlaceholder}
                          required
                          className="border-slate-200"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6 mb-6">
                      <div>
                        <Label className="text-slate-700 mb-2 block">{content.form.company}</Label>
                        <Input
                          name="empresa"
                          value={formData.empresa}
                          onChange={handleChange}
                          placeholder={content.form.companyPlaceholder}
                          className="border-slate-200"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700 mb-2 block">{content.form.subject} *</Label>
                        <Input
                          name="assunto"
                          value={formData.assunto}
                          onChange={handleChange}
                          placeholder={content.form.subjectPlaceholder}
                          required
                          className="border-slate-200"
                        />
                      </div>
                    </div>

                    <div className="mb-6">
                      <Label className="text-slate-700 mb-2 block">{content.form.message} *</Label>
                      <textarea
                        name="mensagem"
                        value={formData.mensagem}
                        onChange={handleChange}
                        placeholder={content.form.messagePlaceholder}
                        required
                        rows={5}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-blue-600 text-white hover:bg-blue-700 py-6 font-medium"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          {content.form.sending}
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="w-5 h-5" />
                          {content.form.submit}
                        </span>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#0B0F19] py-12 mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white font-chivo">OMBUDNOW</span>
            </div>
            <p className="text-slate-500 text-sm">
              © 2025 OmbudNow. {currentLang === 'en' ? 'All rights reserved.' : 'Todos os direitos reservados.'}
            </p>
            <div className="flex items-center gap-6">
              <Link to={paths.privacy} className="text-slate-500 hover:text-white text-sm transition-colors">
                {currentLang === 'en' ? 'Privacy' : 'Privacidade'}
              </Link>
              <Link to={paths.terms} className="text-slate-500 hover:text-white text-sm transition-colors">
                {currentLang === 'en' ? 'Terms' : 'Termos'}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
